import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronLeft, ChevronRight, Play } from "lucide-react";
import { useContent } from "../../context/ContentContext";
import { galleryCover, mediaItem } from "../../lib/media";
import { FramedImage } from "./FramedImage";
import { isPast, pastBadgeLabel } from "../../lib/publish";
import { NewsModal } from "./NewsModal";

// Home selection: items flagged showOnHome, in the admin-defined order (pinned first, then manual order)
const homeItems = (news) =>
  (news?.items || [])
    .map((it, i) => ({ ...it, _i: i }))
    .filter((it) => !["hidden", "archived"].includes(it.status || "published") && it.showOnHome !== false)
    .sort((a, b) => {
      if (!!a.pinned !== !!b.pinned) return a.pinned ? -1 : 1;
      const ao = a.order ?? a._i, bo = b.order ?? b._i;
      if (ao !== bo) return ao - bo;
      return a._i - b._i;
    });

export const LatestNews = () => {
  const { content } = useContent();
  const news = content.news || {};
  const scroller = useRef(null);
  const [active, setActive] = useState(null);
  if (news.showOnHome === false) return null;
  const items = homeItems(news);
  if (items.length === 0) return null;

  const scroll = (dir) => scroller.current?.scrollBy({ left: dir * Math.min(scroller.current.clientWidth * 0.85, 720), behavior: "smooth" });

  return (
    <section id="latest-news" className="py-16 md:py-20 border-y border-white/10 bg-[#070707] overflow-hidden" data-testid="latest-news">
      <div className="px-6 md:px-12 mb-8 flex items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5 mb-2">
            <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-70 animate-ping" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" /></span>
            <span className="text-[11px] tracking-[0.35em] uppercase text-electric">{news.homeEyebrow || "En ce moment chez Karams"}</span>
          </div>
          <h2 className="font-serif text-3xl md:text-4xl text-white">{news.homeHeading || "Dernières actualités"}</h2>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {items.length > 1 && <>
            <button onClick={() => scroll(-1)} data-testid="latest-prev" aria-label="Précédent" className="flex h-10 w-10 rounded-full border border-white/20 text-white items-center justify-center hover:border-electric hover:text-electric transition-colors"><ChevronLeft size={18} /></button>
            <button onClick={() => scroll(1)} data-testid="latest-next" aria-label="Suivant" className="flex h-10 w-10 rounded-full border border-white/20 text-white items-center justify-center hover:border-electric hover:text-electric transition-colors"><ChevronRight size={18} /></button>
          </>}
          <Link to="/#news" className="hidden md:inline-flex items-center gap-2 text-[11px] tracking-[0.15em] uppercase text-secondary2 hover:text-electric transition-colors ml-2">Tout voir <ArrowUpRight size={15} /></Link>
        </div>
      </div>

      <div ref={scroller} className="flex gap-5 overflow-x-auto snap-x snap-mandatory px-6 md:px-12 scroll-pl-6 md:scroll-pl-12 pb-4 scrollbar-none" style={{ scrollbarWidth: "none" }}>
        {items.map((it, i) => {
          const cover = it.mediaType === "slideshow" ? galleryCover(it.gallery) : it.image;
          const fm = it.mediaType === "slideshow" ? mediaItem((it.gallery || [])[0]) : null;
          const isVideoCover = it.mediaType === "video" || (fm && fm.type === "video");
          const past = isPast(it);
          return (
            <button key={it.id || i} onClick={() => setActive(it)} data-testid={`latest-news-card-${i}`}
              className="group/card text-left snap-start shrink-0 w-[280px] md:w-[340px] rounded-lg overflow-hidden border border-white/10 bg-surface/60 hover:border-electric/40 transition-colors">
              <div className={`relative h-44 overflow-hidden bg-[#0e0e0e] ${past ? "grayscale-[0.35]" : ""}`}>
                {cover && <div className="w-full h-full transition-transform duration-700 group-hover/card:scale-105"><FramedImage src={cover} frame={it.imageFrame} alt={it.title} /></div>}
                {past && <span className="absolute top-3 right-3 text-[9px] tracking-[0.2em] uppercase text-purple-200 bg-black/60 backdrop-blur px-2.5 py-1 rounded-full border border-purple-400/40">{pastBadgeLabel(it)}</span>}
                {isVideoCover && <span className="absolute inset-0 flex items-center justify-center"><span className="h-11 w-11 rounded-full bg-black/50 border border-white/40 flex items-center justify-center text-white group-hover/card:border-electric group-hover/card:text-electric transition-colors"><Play size={16} className="ml-0.5" /></span></span>}
              </div>
              <div className="p-5">
                <div className="text-[10px] tracking-[0.2em] uppercase text-electric mb-2">{it.category}{it.date ? ` · ${it.date}` : ""}</div>
                <h3 className="font-serif text-lg text-white leading-snug line-clamp-2">{it.title}</h3>
              </div>
            </button>
          );
        })}
      </div>

      <NewsModal item={active} onClose={() => setActive(null)} />
    </section>
  );
};
